import { useEffect } from 'react';
import { useStores } from 'index';

/**
 * Hook to preload and manage SAS token caching on app initialization and org switches.
 * 
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const organizationId = useStores().profileStore.organizationId;
 *   useSasTokenPreloader(organizationId);
 *   // Now SAS token is preloaded and cached
 * }
 * ```
 * 
 * The hook will:
 * - Fetch a fresh token on first load
 * - Re-fetch if org switches
 * - Automatically refresh before token expiration
 */
export const useSasTokenPreloader = (organizationId: string | null | undefined) => {
  const { sasTokenStore, apiServiceAxiosInstance } = useStores();

  useEffect(() => {
    if (!organizationId) {
      return; // Wait until org ID is available
    }

    // Load token for current org
    void sasTokenStore.LoadToken(apiServiceAxiosInstance, organizationId);

    // Refresh token periodically (every 5 minutes) to catch expiration
    // This gives us time to refresh before the 5-minute-before-expiry buffer kicks in
    const refreshInterval = setInterval(() => {
      void sasTokenStore.LoadToken(apiServiceAxiosInstance, organizationId);
    }, 5 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [organizationId, sasTokenStore, apiServiceAxiosInstance]);
};
